/**
 * Returns the local calendar date as a YYYY-MM-DD string.
 * Use this instead of toISOString().split("T")[0] which returns a UTC date
 * and will show the wrong day for users west of UTC (e.g. US timezones).
 */
export function localDateStr(date: Date = new Date()): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

/**
 * Returns a local datetime string formatted for <input type="datetime-local">.
 * Use this instead of toISOString().slice(0, 16) which shifts to UTC time
 * and will show the wrong time for users in non-UTC timezones.
 */
export function localDateTimeStr(date: Date): string {
  return `${localDateStr(date)}T${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

/**
 * Calendar-day difference between two dates in LOCAL time (b − a), where
 * 0 = same calendar day, 1 = b is one day after a, etc. Anchors both to
 * local midnight first.
 *
 * Use this for "Today / Yesterday / N days ago" style labels. The naive
 * `Math.floor((b - a) / 86400000)` counts elapsed 24-hour periods, NOT
 * calendar days — so something logged yesterday at 3pm reads as "Today"
 * until a full 24h has literally passed (founder report 2026-06-26,
 * workout history). Calendar-anchoring fixes that.
 */
export function calendarDayDiff(a: Date, b: Date): number {
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  return Math.round((startOfDay(b).getTime() - startOfDay(a).getTime()) / 86_400_000);
}

/**
 * "Today" / "Yesterday" / "N days ago" / "Mon D" for a past date, computed
 * on calendar-day boundaries in local time (see calendarDayDiff). Don't
 * use elapsed-time math for these labels — it produces off-by-one days
 * around the midnight boundary.
 */
export function formatRelativeDay(date: string | Date, now: Date = new Date()): string {
  const d = new Date(date);
  const diffDays = calendarDayDiff(d, now);
  if (diffDays <= 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/**
 * The day after a YYYY-MM-DD key, as a YYYY-MM-DD key.
 *
 * Builds the date with the LOCAL constructor `new Date(y, m, d)` rather than
 * parsing the string. `new Date("2026-08-16")` is parsed as UTC midnight, which
 * lands on the 15th for everyone west of UTC — the bug this whole file exists
 * to prevent. Month and year rollover come free: JS normalises `new Date(2026,
 * 11, 32)`.
 */
export function nextDateKey(key: string): string {
  const [y, m, d] = key.split("-").map(Number);
  return localDateStr(new Date(y, m - 1, d + 1));
}
